import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Heart, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Memoized color mapping outside component
const COLOR_MAP = {
  'red': '#ef4444',
  'blue': '#113063ff', 
  'green': '#104921ff',
  'black': '#000000',
  'white': '#ffffff',
  'brown': '#351b1bff',
  'tan': '#ca9754ff',
  'navy': '#000080'
};

// Pre-calculate fallback styles
const FALLBACK_STYLE = { backgroundColor: '#d1d5db' };

const ProductCard = memo(({ product, eagerLoad = false }) => {
  const navigate = useNavigate(); 
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [currentImage, setCurrentImage] = useState('');
  const [currentStock, setCurrentStock] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const { 
    id,
    name, 
    PName,
    PCategories = [],
    category,
    PType = [],
    PRating = 0,
    PDateAdded
  } = product;

  // Memoized navigation handler
  const handleButtonClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (id) {
      navigate(`/product/${id}`);
    }
  }, [id, navigate]);

  // Memoized product data
  const productName = useMemo(() => name || PName || "Unnamed Product", [name, PName]);
  const categoryName = useMemo(() => 
    category || (PCategories.length > 0 ? PCategories[0] : "Uncategorized"), 
    [category, PCategories]
  );

  // Memoized color variants with optimized calculation
  const colorVariants = useMemo(() => {
    if (!PType || PType.length === 0) return [];
    
    const colorMap = new Map();
    
    for (let i = 0; i < PType.length; i++) {
      const variant = PType[i];
      const color = variant.PSoleColor || variant.PTypeName || "Default";
      if (color && !colorMap.has(color)) {
        colorMap.set(color, variant);
      }
    }
    
    return Array.from(colorMap.entries()).map(([color, variant]) => ({
      color,
      variant,
      // Pre-calculate style here
      style: { 
        backgroundColor: COLOR_MAP[color.toLowerCase()] || FALLBACK_STYLE.backgroundColor 
      }
    }));
  }, [PType]);

  // Memoized price calculation
  const priceInfo = useMemo(() => {
    if (!selectedVariant) return { price: 0, originalPrice: 0, discount: 0 };
    
    const price = selectedVariant.PPrice || 0;
    const discountPrice = selectedVariant.PInOffer ? (selectedVariant.PDiscountPrice || 0) : null;
    
    if (discountPrice && discountPrice < price) {
      const discount = Math.round(((price - discountPrice) / price) * 100);
      return {
        price: discountPrice,
        originalPrice: price,
        discount: discount
      };
    }
    
    return {
      price: price,
      originalPrice: null,
      discount: 0
    };
  }, [selectedVariant]);

  // Memoized "isNew" calculation
  const isProductNew = useMemo(() => {
    if (!PDateAdded) return false;
    try {
      const addedDate = new Date(PDateAdded);
      const daysSinceAdded = (new Date() - addedDate) / (1000 * 60 * 60 * 24);
      return daysSinceAdded < 30;
    } catch {
      return false;
    }
  }, [PDateAdded]);

  // Memoized stock update function
  const updateStockInfo = useCallback((variant) => {
    if (!variant || !variant.PStock) {
      setCurrentStock(0);
      return;
    }

    let totalStock = 0;
    for (let i = 0; i < variant.PStock.length; i++) {
      totalStock += (variant.PStock[i].Qty || 0);
    }
    setCurrentStock(totalStock);
  }, []);

  // Memoized variant selection handler
  const handleVariantSelect = useCallback((variant) => {
    setSelectedVariant(variant);
    setCurrentImage(variant.PImgUrls?.[0] || '');
    setImageLoaded(false);
    setImageError(false);
    updateStockInfo(variant);
  }, [updateStockInfo]);

  // Memoized image handlers
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const handleImageError = useCallback(() => {
    setImageError(true);
    setImageLoaded(true);
  }, []);

  // Initialize selected variant and stock - optimized
  useEffect(() => {
    if (PType && PType.length > 0 && !selectedVariant) {
      const initialVariant = PType[0];
      setSelectedVariant(initialVariant);
      setCurrentImage(initialVariant.PImgUrls?.[0] || '');
      updateStockInfo(initialVariant);
    }
  }, [PType, selectedVariant, updateStockInfo]);

  // Memoized star rendering function
  const renderStars = useCallback((ratingValue = PRating) => {
    const stars = [];
    const floorRating = Math.floor(ratingValue);
    
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Star
          key={i}
          size={14}
          fill={i <= floorRating ? "darktaupe" : "currentColor"} 
          stroke="tan"
          strokeWidth={1}
          className={i <= floorRating ? "text-darktaupe" : "text-tan"}
        />
      );
    }
    return stars;
  }, [PRating]);

  // Loading state
  if (!selectedVariant) {
    return (
      <div className="relative bg-beige group overflow-hidden border border-taupe rounded-lg shadow-sm animate-pulse product-card">
        <div className="h-56 bg-gray-300"></div>
        <div className="p-3">
          <div className="h-4 bg-gray-300 rounded mb-2"></div>
          <div className="h-6 bg-gray-300 rounded mb-2"></div>
          <div className="h-10 bg-gray-300 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-beige group overflow-hidden border border-taupe rounded-lg shadow-sm product-card">
      
      {/* Product Image and Overlay */}
      <a href={`/product/${id}`} className="relative block h-56 overflow-hidden bg-offwhite">
        {/* Image with loading state */}
        <img
          src={imageError ? '/placeholder-image.jpg' : currentImage}
          alt={productName}
          className={`w-full h-full object-cover object-center transition-all duration-300 group-hover:scale-105 product-image ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={handleImageLoad}
          onError={handleImageError}
          loading={eagerLoad ? "eager" : "lazy"}
          decoding="async"
          width="300"
          height="224"
        />
        
        {/* Loading skeleton */}
        {!imageLoaded && !imageError && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse"></div>
        )}
        
        {/* Wishlist Icon */}
        <button 
          className="absolute top-3 right-3 z-10 bg-beige p-1.5 rounded-full shadow-md text-charcoal hover:text-redburnt transition-colors"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Heart size={18} strokeWidth={1} />
        </button>
        
        {/* NEW Tag */}
        {isProductNew && (
          <span className="absolute top-3 left-3 z-10 bg-primary text-offwhite text-[10px] px-2 py-0.5 rounded-sm font-semibold">
            NEW
          </span>
        )}

        {/* Out of Stock Overlay */}
        {currentStock === 0 && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20">
            <span className="bg-white text-gray-800 px-4 py-2 rounded-lg font-semibold text-sm">
              Out of Stock
            </span>
          </div>
        )}
      </a>

      {/* Product Details */}
      <div className="p-3 flex flex-col items-start text-left">
        <p className="text-xs text-charcoal/70 mb-1">{categoryName}</p>
        <h3 className="text-lg font-bold text-charcoal mb-2 line-clamp-2">{productName}</h3>
        
        {/* Rating */}
        {PRating > 0 && (
          <div className="flex items-center mb-3">
            <div className="flex text-darktaupe mr-1">
              {renderStars(PRating)}
            </div>
            <span className="text-xs text-charcoal/70">
              {PRating % 1 === 0 ? PRating.toString() : PRating.toFixed(1)}
            </span>
          </div>
        )}
        
        {/* Color Swatches - Using pre-calculated styles */}
        {colorVariants.length > 0 && (
          <div className="flex space-x-1 mb-3 flex-wrap gap-1">
            {colorVariants.map(({ color, variant, style }, index) => (
              <button 
                key={`${color}-${index}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleVariantSelect(variant);
                }}
                className={`w-4 h-4 rounded-full border border-offwhite shadow-sm ring-1 ring-charcoal/40 hover:ring-primary transition-all flex-shrink-0 ${
                  selectedVariant.id === variant.id ? 'ring-2 ring-primary scale-110' : ''
                }`}
                style={style}
                aria-label={`Select ${color} color`}
              />
            ))}
          </div>
        )}

        {/* Price and Discount */}
        <div className="flex items-center space-x-2 mb-3 flex-wrap">
          <span className="text-lg font-bold text-primary">PKR {priceInfo.price}</span>
          {priceInfo.originalPrice && (
            <>
              <span className="text-xs text-charcoal/55 line-through">PKR {priceInfo.originalPrice}</span>
              <span className="bg-redburnt/10 text-redburnt text-[10px] px-1.5 py-0.5 rounded-full font-semibold">
                -{priceInfo.discount}%
              </span>
            </>
          )}
        </div>

        {/* Add To Cart / Out of Stock Button */}
        {currentStock > 0 ? (
          <button 
            onClick={handleButtonClick}
            className="w-full bg-primary text-offwhite text-sm py-2 rounded-md hover:bg-primary-dark transition-colors font-semibold focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            View Details
          </button>
        ) : (
          <button 
            className="w-full bg-gray-400 text-gray-700 text-sm py-2 rounded-md cursor-not-allowed font-semibold"
            disabled
          >
            Out of Stock
          </button>
        )}
      </div>
    </div>
  );
});

ProductCard.displayName = 'ProductCard';

export default ProductCard;